// src/components/CookieConsentBanner.tsx
import React, { FC, useState, useEffect, useRef } from 'react';
import i18nEnglish from '../../i18n/en';
import i18nSwiss   from '../../i18n/de-ch';
import i18nFrench  from '../../i18n/fr';
import i18nItalian from '../../i18n/it';
import { supportedLanguages } from '../../config/config';
import './index.css';

const CONSENT_KEY    = 'cookie-consent';
const COOKIE_DOMAIN  = '.elektrobedarf.ch';
const COOKIE_MAX_AGE = 365 * 24 * 60 * 60; // seconds

// Production‐only endpoints
const AMBIENT_IFRAMES = [
  'https://solar-ratgeber.ch/consent.html/',
  'https://elektrobedarf.ch/consent.html/',
];

const CookieConsentBanner: FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const iframeRefs = useRef<Record<string, HTMLIFrameElement | null>>({});

  const writeElektroCookie = (val: string) => {
    document.cookie = [
      `${CONSENT_KEY}=${encodeURIComponent(val)}`,
      `path=/`,
      `domain=${COOKIE_DOMAIN}`,
      `max-age=${COOKIE_MAX_AGE}`,
      `SameSite=Lax`,
    ].join('; ');
  };

  const readElektroCookie = (): string | null => {
    const m = document.cookie.match(
      new RegExp('(?:^|; )' + CONSENT_KEY + '=([^;]+)')
    );
    return m ? decodeURIComponent(m[1]) : null;
  };

  const getStoredConsent = (): string | null => {
    return localStorage.getItem(CONSENT_KEY) || readElektroCookie();
  };

  const broadcastConsent = (val: string) => {
    localStorage.setItem(CONSENT_KEY, val);
    writeElektroCookie(val);

    AMBIENT_IFRAMES.forEach(src => {
      const fr = iframeRefs.current[src];
      if (fr?.contentWindow) {
        fr.contentWindow.postMessage(
          { type: 'set-cookie-consent', value: val },
          new URL(src).origin
        );
      }
    });
  };

  useEffect(() => {
    if (getStoredConsent()) {
      setIsVisible(false);
      return;
    }

    setIsVisible(true);

    const onMessage = (e: MessageEvent) => {
      const allowed = AMBIENT_IFRAMES.map(u => new URL(u).origin);
      if (!allowed.includes(e.origin)) return;

      const msg = (e.data as any) || {};
      if (msg.type === 'cookie-consent' && msg.value) {
        if (msg.value === 'accepted' || msg.value === 'declined') {
          broadcastConsent(msg.value);
          setIsVisible(false);
        }
      }
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  const handleAccept = () => {
    broadcastConsent('accepted');
    setIsVisible(false);
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'cookieConsentGiven' });
  };
  const handleDecline = () => {
    broadcastConsent('declined');
    setIsVisible(false);
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'cookieConsentDenied' });
  };

  if (!isVisible) return null;

  const lang =
    (document.documentElement.getAttribute('data-language') as string) ||
    supportedLanguages[0];
  const i18nMap = {
    de:     i18nSwiss,
    'de-ch':i18nSwiss,
    'de-CH':i18nSwiss,
    gsw:    i18nSwiss,
    en:     i18nEnglish,
    fr:     i18nFrench,
    it:     i18nItalian,
  }[lang] || i18nEnglish;

  return (
    <>
      {AMBIENT_IFRAMES.map(src => (
        <iframe
          key={src}
          src={src}
          style={{ display: 'none' }}
          title={`consent-relay-${src}`}
          ref={el => { iframeRefs.current[src] = el }}
        />
      ))}

      <div
        id="cookie-consent-overlay"
        className="cookie-consent-overlay"
        role="dialog"
        aria-labelledby="cookie-consent-banner"
        aria-hidden={!isVisible}
      >
        <p id="cookie-consent-banner">
          {i18nMap.cookieConsentBanner1}<br/>
          {i18nMap.cookieConsentBanner2}
        </p>
        <button
          id="accept-cookies"
          className="accept-cookies"
          onClick={handleAccept}
          aria-label={i18nMap.acceptButtonText}
        >
          {i18nMap.acceptButtonText}
        </button>
        <button
          id="decline-cookies"
          className="decline-cookies"
          onClick={handleDecline}
          aria-label={i18nMap.declineButtonText}
        >
          {i18nMap.declineButtonText}
        </button>
      </div>
    </>
  );
};

export default CookieConsentBanner;

